import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { trainingApi } from '../services/api';

function ModuleDetail() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchModuleDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const fetchModuleDetails = async () => {
    try {
      setLoading(true);
      const moduleRes = await trainingApi.getModules();
      
      if (moduleRes.success) {
        const foundModule = moduleRes.data.find(m => m.id === parseInt(moduleId));
        setModule(foundModule);
      }
    } catch (err) {
      console.error('Error fetching module details:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Module not found</h2>
        <button onClick={() => navigate('/modules')} className="mt-4 text-blue-600 hover:text-blue-800">
          ← Back to Modules
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={() => navigate('/modules')} className="text-blue-600 hover:text-blue-800 mb-6 flex items-center">
        <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Modules
      </button>

      {/* Module Header */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-8 py-12 text-white">
          <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">{module.category || 'Training'}</span>
          <h1 className="text-4xl font-bold mt-4">{module.title}</h1>
          <p className="text-blue-100 mt-2 text-lg">{module.description}</p>
        </div>

        <div className="px-8 py-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <p className="text-sm text-gray-600">Duration</p>
              <p className="text-xl font-bold text-gray-900">{module.duration_hours || module.estimated_hours || '4'} hours</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Prerequisites</p>
              <p className="text-xl font-bold text-gray-900">{module.prerequisites || 'None'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Certification Track</p>
              <p className="text-xl font-bold text-gray-900">{module.certification_track || 'Both'}</p>
            </div>
          </div>
        </div>

        {/* Learning Objectives */}
        {module.learning_objectives && (
          <div className="px-8 py-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Learning Objectives</h2>
            <ul className="space-y-2">
              {module.learning_objectives.split(';').filter(o => o.trim()).map((obj, idx) => (
                <li key={idx} className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-700">{obj.trim()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* SOP Reference */}
        {module.sop_reference && (
          <div className="px-8 py-6 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Related SOP</h3>
            <p className="text-gray-700">{module.sop_reference}</p>
          </div>
        )}

        {/* Content Preview */}
        {module.content && (
          <div className="px-8 py-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Module Overview</h2>
            <div className="bg-gray-50 p-4 rounded max-h-96 overflow-y-auto">
              <pre className="text-sm whitespace-pre-wrap font-sans text-gray-700">
                {module.content.substring(0, 800)}...
              </pre>
            </div>
          </div>
        )}

        {/* PMI Integration */}
        {module.capm_domains && (
          <div className="px-8 py-6 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">PMI CAPM Domains</h3>
            <div className="flex flex-wrap gap-2">
              {module.capm_domains.split(',').map((domain, idx) => (
                <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
                  {domain.trim()}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="px-8 py-6 flex gap-4">
          <Link
            to={`/module/${module.module_code}`}
            className="flex-1 text-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            📖 Start Module
          </Link>
          <Link
            to="/modules"
            className="flex-1 text-center bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors"
          >
            ← Back to Library
          </Link>
        </div>
      </div>

    </div>
  );
}

export default ModuleDetail;
